import {
  resolvePatientChatWorkingDraft,
  type PatientChatDraftContext,
} from "../../../lib/patientChat";

interface BuildDraftContextOptions {
  latestReply: string;
  note?: string;
  patientSummary?: string;
  diarizedTranscript?: string;
  noteCitations?: unknown;
  chartHistory?: unknown;
  includeFullChartHistory?: boolean;
  sendEmptyContext?: boolean;
}

const hasContextValue = (value: unknown): boolean => {
  if (typeof value === "string") return value.trim().length > 0;
  if (Array.isArray(value)) return value.length > 0;
  return Boolean(value && typeof value === "object" && Object.keys(value).length > 0);
};

/**
 * The server falls back to the patient's stored value for any field left out, and
 * treats a field that is sent (even an empty string) as the page's current value.
 */
export const buildDraftContext = ({
  latestReply,
  note,
  patientSummary,
  diarizedTranscript,
  noteCitations,
  chartHistory,
  includeFullChartHistory = false,
  sendEmptyContext = false,
}: BuildDraftContextOptions): PatientChatDraftContext => {
  const shouldSend = (value: unknown) =>
    sendEmptyContext ? value !== undefined : hasContextValue(value);
  const draftContext: PatientChatDraftContext = {};

  const workingDraft = resolvePatientChatWorkingDraft(latestReply, note);
  if (shouldSend(workingDraft)) draftContext.note = workingDraft;
  if (shouldSend(patientSummary)) draftContext.patientSummary = patientSummary;
  if (shouldSend(diarizedTranscript)) draftContext.diarizedTranscript = diarizedTranscript;
  if (shouldSend(noteCitations)) draftContext.noteCitations = noteCitations;
  // With includeFullChartHistory the page has already filtered chart history, so an
  // empty object must still go out or the server would substitute the full history.
  if (
    shouldSend(chartHistory) ||
    (includeFullChartHistory && chartHistory && typeof chartHistory === "object")
  ) {
    draftContext.chartHistory = chartHistory;
  }
  if (includeFullChartHistory) draftContext.includeFullChartHistory = true;

  return draftContext;
};
